/** E13.1 Selbstregistrierung: Laden kostenlos testen – öffentlich unter /registrieren, mit Turnstile, wenn eingerichtet. */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Illustration, Notice, TextField } from "../ui";
import { Marke } from "../ui/Marke";
import { api, ApiFehler, rufe } from "../api/client";
import { RechtlichesFuss } from "./Rechtliches";

export function Registrieren() {
  const info = useQuery({ queryKey: ["public", "registrieren"], queryFn: () => rufe<{ testTage: number; turnstileSiteKey: string | null }>(api.api.public.registrieren.$get()), staleTime: Infinity });
  const [form, setForm] = useState({ ladenName: "", inhaberName: "", email: "", demo: true, datenschutz: false });
  const [token, setToken] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState(false);
  const turnstile = useRef<HTMLDivElement>(null);
  const siteKey = info.data?.turnstileSiteKey ?? null;
  useEffect(() => {
    if (!siteKey || !turnstile.current) return;
    const w = window as unknown as { turnstile?: { render: (el: HTMLElement, o: { sitekey: string; callback: (t: string) => void; theme: string; language: string; size: string }) => void } };
    const rendern = () => w.turnstile?.render(turnstile.current!, { sitekey: siteKey, callback: setToken, theme: "auto", language: "de", size: "flexible" });
    if (w.turnstile) rendern();
    else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = rendern;
      document.head.appendChild(s);
    }
  }, [siteKey]);

  async function senden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (form.ladenName.trim().length < 2) return setFehler("Bitte den Namen des Ladens eingeben.");
    if (form.inhaberName.trim().length < 2) return setFehler("Bitte deinen Namen eingeben.");
    if (!form.email.includes("@")) return setFehler("Bitte eine gültige E-Mail-Adresse eingeben.");
    if (!form.datenschutz) return setFehler("Bitte die Datenschutzerklärung bestätigen.");
    setLaeuft(true);
    try {
      await rufe(api.api.public.registrieren.$post({ json: { ladenName: form.ladenName.trim(), inhaberName: form.inhaberName.trim(), email: form.email.trim(), demo: form.demo, datenschutz: true, turnstile: token ?? undefined } }));
      setFertig(true);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally {
      setLaeuft(false);
    }
  }

  if (fertig) {
    return (
      <div className="app-public">
        <div className="app-public__card sc-center" style={{ padding: 0 }}>
          <Illustration name="all-done" />
          <h1 className="display">Schau in dein Postfach</h1>
          <p>Wir haben dir einen Anmeldelink an {form.email.trim()} geschickt. Damit richtest du {form.ladenName.trim()} in wenigen Minuten ein.</p>
          <p className="sc-muted sc-small">Keine Mail? Prüfe den Spam-Ordner oder lass dir unter „Anmelden“ einen neuen Link schicken.</p>
          <Link to="/anmelden" className="rg-btn rg-btn--secondary">Zur Anmeldung</Link>
          <RechtlichesFuss />
        </div>
      </div>
    );
  }

  return (
    <div className="app-public">
      <form className="app-public__card" onSubmit={(e) => void senden(e)} noValidate>
        <div className="app-public__brand">
          <Marke size={40} />
          <span className="sc-strong">MeinRegalladen</span>
        </div>
        <h1 className="title">Laden kostenlos testen</h1>
        <p className="sc-muted">{info.data?.testTage ?? 14} Tage mit allem, was dazugehört: Regale, Mieter, Kasse mit SumUp, Abrechnungen. Keine Zahlungsdaten nötig. Nach der Testphase entscheidest du, ob es weitergeht.</p>
        <TextField label="Name des Ladens" value={form.ladenName} onChange={(e) => setForm({ ...form, ladenName: e.target.value })} autoComplete="organization" autoFocus maxLength={80} />
        <TextField label="Dein Name" value={form.inhaberName} onChange={(e) => setForm({ ...form, inhaberName: e.target.value })} autoComplete="name" maxLength={80} />
        <TextField label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" inputMode="email" hint="Damit meldest du dich an – ohne Passwort, per Link." />
        <label className="sc-check"><input type="checkbox" checked={form.demo} onChange={(ev) => setForm({ ...form, demo: ev.target.checked })} /> Beispielregale und Ladenware anlegen (lassen sich jederzeit löschen)</label>
        <label className="sc-check"><input type="checkbox" checked={form.datenschutz} onChange={(ev) => setForm({ ...form, datenschutz: ev.target.checked })} /> Ich habe die <Link to="/datenschutz">Datenschutzerklärung</Link> gelesen.</label>
        {siteKey ? <div ref={turnstile} /> : null}
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <Button type="submit" variant="primary" size="lg" block loading={laeuft} disabled={!!siteKey && !token}>Laden anlegen</Button>
        <p className="sc-muted sc-small">Schon dabei? <Link to="/anmelden">Anmelden</Link></p>
        <RechtlichesFuss />
      </form>
    </div>
  );
}
